import React, { useEffect } from "react"
import { styled, Theme } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import { useAcceptFollowRequestMutation, useRejectFollowRequestMutation } from "appRedux/slices/userApiSlice"
import TickIcon from "components/atoms/_icons/TickIcon"
import CancelIcon from "components/atoms/_icons/CancelIcon"
import CircularProgress from "components/atoms/CircularProgress"

type FollowRequestButtonsProps = {
  id?: number;
  followerId: number;
  onAccept?: (notificationId: number, followerId: number, status: FollowStatus) => void;
  onReject?: (notificationId: number, followerId: number) => void;
}

export default function FollowRequestButtons(props: FollowRequestButtonsProps) {
  const {
    id,
    followerId,
    onAccept = new Function(),
    onReject = new Function()
  } = props

  const [acceptFollowRequest, {
    data: acceptFollowRequestResult,
    isLoading: isFollowRequestAcceptLoading,
    isSuccess: followRequestAccepted
  }] = useAcceptFollowRequestMutation()

  const [rejectFollowRequest, {
    isLoading: isFollowRequestRejectLoading,
    isSuccess: followRequestRejected
  }] = useRejectFollowRequestMutation()

  useEffect(() => {
    if (!followRequestRejected) return
    onReject(id, followerId)
  }, [followRequestRejected])

  useEffect(() => {
    if (!followRequestAccepted) return
    // @ts-ignore
    const status = acceptFollowRequestResult?.followStatus || null
    onAccept(id, followerId, status)
  }, [followRequestAccepted])

  function handleAcceptFollowRequest() {
    acceptFollowRequest(followerId)
  }

  function handleRejectFollowRequest() {
    rejectFollowRequest(followerId)
  }

  return (
    <FollowIconButtonsWrapper id="follow-icon-buttons-wrapper">
      <IconWrapper
        severity="success"
        loading={isFollowRequestAcceptLoading ? 1 : 0}
        disabled={isFollowRequestRejectLoading}
        onClick={handleAcceptFollowRequest}
      >
        {isFollowRequestAcceptLoading ? <CircularProgress/> : <TickIcon/>}
      </IconWrapper>
      <IconWrapper
        severity="error"
        loading={isFollowRequestRejectLoading ? 1 : 0}
        disabled={isFollowRequestAcceptLoading}
        onClick={handleRejectFollowRequest}
      >
        {isFollowRequestRejectLoading ? <CircularProgress/> : <CancelIcon/>}
      </IconWrapper>
    </FollowIconButtonsWrapper>
  )
}

const FollowIconButtonsWrapper = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "8px"
}))

type IconWrapperProps = {
  theme?: Theme;
  severity: "success" | "error";
  loading: 0 | 1;
}

const IconWrapper = styled("button")(({ theme, severity, loading }: IconWrapperProps) => ({
  width: "40px",
  height: "40px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  backgroundColor: loading ? "transparent" : theme?.palette[severity]["1"],
  border: "none",
  outline: "none",
  position: "relative",
  cursor: "pointer",

  "& svg path": {
    fill: severity === "error" ? theme?.palette.error[4] : theme?.palette.success[4]
  },

  "&[disabled]": {
    backgroundColor: theme?.palette.gray["6"],

    "& svg": {
      "& path": {
        fill: theme?.palette.gray["10"]
      }
    }

  }
}))

